import React, { useCallback, useState } from 'react';
import { Button, Loader, UnstyledButton, Text, useMantineTheme } from '@mantine/core';
import { IconCamera } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useScanner, DetectLocationFn } from '@/hooks';
import { useToast } from '@/hooks';
import { ReceiptScanResult } from '@/utils/api';
import { CameraCapture } from '@/components/generic';
import { ScanProgressOverlay } from '@/components/features';

// ============================================
// Constants
// ============================================

/** Delay (ms) before auto-dismissing the progress overlay on success */
const AUTO_DISMISS_DELAY_MS = 1200;

// ============================================
// Types
// ============================================

export interface ScanReceiptButtonProps {
  carId?: string | null;

  /** Called when a receipt is successfully scanned and processed */
  onScanComplete: (result: ReceiptScanResult, carId?: string | null) => void;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /**
   * Optional async function for geocoding/location detection.
   * If provided, it's called during the scanning pipeline when
   * the receipt contains an address. Should return the enriched result.
   */
  onDetectLocation?: DetectLocationFn;

  /** Size variant — "mobile" renders a brighter, full-width style matching quick actions */
  size?: 'default' | 'mobile';
}

// ============================================
// Component
// ============================================

/**
 * Scan Receipt button that opens a full-screen camera capture,
 * sends the image for AI recognition with real-time progress,
 * and returns the parsed result to the parent.
 *
 * Flow:
 * 1. User taps button → CameraCapture opens
 * 2. User takes photo or picks from gallery → preview with Retake/Use
 * 3. User confirms → CameraCapture closes, ScanProgressOverlay shows
 * 4. Pipeline: uploading → analyzing → detecting_location → complete
 * 5. On complete → overlay auto-dismisses, onScanComplete called
 * 6. On error → overlay shows error, tap to dismiss
 */
export const ScanReceiptButton: React.FC<ScanReceiptButtonProps> = ({
  onScanComplete,
  disabled = false,
  onDetectLocation,
  carId,
  size = 'default',
}) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const toast = useToast();
  const theme = useMantineTheme();

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);

  const {
    scanning,
    progress,
    error: scanError,
    scanReceiptBlob,
    reset: resetScanner,
  } = useScanner({ onDetectLocation });

  // ============================================
  // Handlers
  // ============================================

  /** Open the camera overlay */
  const handleButtonClick = useCallback(() => {
    resetScanner();
    setIsCameraOpen(true);
  }, [resetScanner]);

  /** Close the camera without capturing */
  const handleCameraClose = useCallback(() => {
    setIsCameraOpen(false);
  }, []);

  /** User confirmed a photo — close camera, start scanning */
  const handleCapture = useCallback(
    async (blob: Blob) => {
      // Close camera, open progress overlay
      setIsCameraOpen(false);
      setIsProgressOpen(true);

      const result = await scanReceiptBlob(blob);

      if (!result) {
        // Error state — overlay will show error, user taps to dismiss
        return;
      }

      if (!result.success) {
        toast.error(
          result.failureReason ||
          t('common:scanReceipt.unrecognized', 'Could not recognize the receipt. Please try a clearer photo.')
        );
        setIsProgressOpen(false);
        resetScanner();
        return;
      }

      // Success — auto-dismiss after a brief pause so user sees "complete"
      setTimeout(() => {
        setIsProgressOpen(false);

        toast.success(
          t('common:scanReceipt.success', {
            defaultValue: 'Receipt recognized: {{description}}',
            description: result.description || 'Unknown document',
            autoClose: 1000
          })
        );

        onScanComplete(result, carId);
        resetScanner();
      }, AUTO_DISMISS_DELAY_MS);
    },
    [scanReceiptBlob, onScanComplete, carId, toast, t, resetScanner]
  );

  /** Dismiss the progress overlay (error or manual dismiss) */
  const handleProgressDismiss = useCallback(() => {
    setIsProgressOpen(false);
    resetScanner();
  }, [resetScanner]);

  // ============================================
  // Button label
  // ============================================

  const buttonLabel = t('common:scanReceipt.button', 'Scan Receipt');
  const isDisabled = disabled || scanning;

  // ============================================
  // Render
  // ============================================

  // Mobile: brighter style matching VehicleCardQuickActions mobile buttons
  const mobileButton = (
    <UnstyledButton
      onClick={handleButtonClick}
      disabled={isDisabled}
      aria-label={buttonLabel}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: 48,
        width: '100%',
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors.brand[1],
        transition: 'background-color 150ms ease, transform 100ms ease',
        opacity: isDisabled ? 0.6 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) e.currentTarget.style.backgroundColor = theme.colors.brand[2];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.backgroundColor = theme.colors.brand[1];
      }}
      onMouseDown={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.transform = 'scale(0.98)';
          e.currentTarget.style.backgroundColor = theme.colors.brand[3];
        }
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        if (!isDisabled) e.currentTarget.style.backgroundColor = theme.colors.brand[2];
      }}
      onTouchStart={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.transform = 'scale(0.98)';
          e.currentTarget.style.backgroundColor = theme.colors.brand[3];
        }
      }}
      onTouchEnd={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.backgroundColor = theme.colors.brand[1];
      }}
    >
      {scanning ? (
        <Loader size={20} color={theme.colors.brand[7]} />
      ) : (
        <IconCamera size={22} color={theme.colors.brand[7]} stroke={1.8} />
      )}
      <Text fw={600} size="sm" c={theme.colors.brand[7]}>
        {buttonLabel}
      </Text>
    </UnstyledButton>
  );

  // Default: standard Mantine Button
  const defaultButton = (
    <Button
      variant="light"
      leftSection={scanning ? <Loader size="xs" /> : <IconCamera size={18} />}
      onClick={handleButtonClick}
      disabled={isDisabled}
      loading={scanning}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {/* Trigger button */}
      {size === 'mobile' ? mobileButton : defaultButton}

      {/* Full-screen camera capture */}
      <CameraCapture
        opened={isCameraOpen}
        onClose={handleCameraClose}
        onCapture={handleCapture}
        title={t('common:scanReceipt.cameraTitle', 'Scan Receipt')}
        hint={t('common:scanReceipt.cameraHint', 'Position receipt within the frame')}
        maxDimension={1920}
        quality={0.85}
      />

      {/* Full-screen scanning progress */}
      <ScanProgressOverlay
        opened={isProgressOpen}
        step={progress.step}
        errorMessage={scanError}
        onDismiss={handleProgressDismiss}
      />
    </>
  );
};